import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../store';

// State shape:
// {
//   byPractice: {
//     [practiceId]: {
//       [parentTable]: {
//         [slug]: { column_overrides, filter_overrides, display_overrides, date_overrides, target_users }
//       }
//     }
//   }
// }
const initialState = { byPractice: {} };

const slice = createSlice({
    name: 'builtInTabOverrides',
    initialState,
    reducers: {
        setForParent(state, action) {
            const { practiceId, parentTable, overrides } = action.payload;
            if (!state.byPractice[practiceId]) state.byPractice[practiceId] = {};
            const bySlug = {};
            (overrides || []).forEach((o) => { bySlug[o.slug] = o; });
            state.byPractice[practiceId][parentTable] = bySlug;
        },
        upsertOverride(state, action) {
            const { practiceId, parentTable, slug, data } = action.payload;
            if (!state.byPractice[practiceId]) state.byPractice[practiceId] = {};
            if (!state.byPractice[practiceId][parentTable]) state.byPractice[practiceId][parentTable] = {};
            state.byPractice[practiceId][parentTable][slug] = data;
        },
        removeOverride(state, action) {
            const { practiceId, parentTable, slug } = action.payload;
            const table = state.byPractice[practiceId]?.[parentTable];
            if (table) delete table[slug];
        },
        resetBuiltInTabOverrides(state) {
            Object.assign(state, initialState);
        },
    },
});

export default slice.reducer;
export const { setForParent, upsertOverride, removeOverride, resetBuiltInTabOverrides } = slice.actions;

// ---------- Selectors ----------

// Stable empty-object sentinel — prevents `|| {}` from returning a new reference
// on each call when no overrides are stored, which would re-trigger every useEffect
// that has the selector result in its dependency array.
const EMPTY_OVERRIDES = Object.freeze({});

export const selectOverridesForParent = (state, practiceId, parentTable) =>
    state.builtInTabOverrides?.byPractice?.[practiceId]?.[parentTable] || EMPTY_OVERRIDES;

export const selectOverrideForSlug = (state, practiceId, parentTable, slug) =>
    state.builtInTabOverrides?.byPractice?.[practiceId]?.[parentTable]?.[slug] || null;

// ---------- Thunks ----------

export function loadBuiltInTabOverrides(practiceId, parentTable) {
    return async () => {
        if (!practiceId || !parentTable) return;
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/built-in-tab-overrides/?parent_table=${encodeURIComponent(parentTable)}`
            );
            if (res.status === 200) {
                const overrides = res.data?.results || res.data || [];
                dispatch(setForParent({ practiceId, parentTable, overrides }));
            }
        } catch (_) { /* silent */ }
    };
}

export function upsertBuiltInTabOverride(practiceId, parentTable, slug, payload) {
    return async () => {
        if (!practiceId || !parentTable || !slug) return null;
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/built-in-tab-overrides/${encodeURIComponent(slug)}/?parent_table=${encodeURIComponent(parentTable)}`,
                'patch',
                payload
            );
            if (res.status >= 200 && res.status < 300) {
                dispatch(upsertOverride({ practiceId, parentTable, slug, data: res.data }));
                return res.data;
            }
            return null;
        } catch (_) { return null; }
    };
}

export function resetBuiltInTabOverride(practiceId, parentTable, slug) {
    return async () => {
        if (!practiceId || !parentTable || !slug) return;
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/built-in-tab-overrides/${encodeURIComponent(slug)}/?parent_table=${encodeURIComponent(parentTable)}`,
                'delete'
            );
            if (res.status === 204 || res.status === 200) {
                dispatch(removeOverride({ practiceId, parentTable, slug }));
            }
        } catch (_) { /* silent */ }
    };
}
